// Reservation Service
// Handles storage and retrieval of reservations for both guests and members

export interface ReservationData {
  id: string;
  userId?: string; // undefined for guest reservations, user ID for member reservations
  guestName?: string; // For guest reservations
  guestEmail: string;
  guestPhone?: string;
  itemType: 'accommodation' | 'activity' | 'package';
  itemId: string;
  itemName: string;
  checkIn: string; // ISO date string
  checkOut: string; // ISO date string
  guests: number;
  totalPrice: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  paymentStatus: 'pending' | 'paid' | 'refunded';
  paymentMethod: string;
  /** 30% deposit charged upfront for partial payment plan */
  depositAmount?: number;
  /** 70% balance owed at arrival for partial payment plan */
  balanceAmount?: number;
  /** GST (TPS) amount */
  gstAmount?: number;
  /** QST (TVQ) amount */
  qstAmount?: number;
  /** Taxe d'hébergement (3.75%) amount */
  taxeHebergementAmount?: number;
  specialRequests?: string;
  /** Where the client heard about the lodge (checkout / guest form). */
  hearAboutUs?: string;
  createdAt: string; // ISO date string
  isGuest: boolean; // true for guest reservations, false for member reservations
  duration?: number; // Duration in nights/days
  groups?: Array<{ id: string; adults: number; kids: number }>; // Group details for travel
  checkoutId?: string; // ID to group multiple reservations from the same checkout into one order
}

const STORAGE_KEY = 'camping_reservations';

// Get all reservations from localStorage
export const getAllReservations = (): ReservationData[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading reservations from storage:', error);
    return [];
  }
};

// Save a new reservation
export const saveReservation = (reservation: Omit<ReservationData, 'id' | 'createdAt'>): ReservationData => {
  const reservations = getAllReservations();
  const newReservation: ReservationData = {
    ...reservation,
    id: `res_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString()
  };
  
  reservations.push(newReservation);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reservations));
  
  // Dispatch custom event to notify other components
  window.dispatchEvent(new CustomEvent('reservationSaved', { detail: newReservation }));
  
  return newReservation;
};

// Get reservations for a specific user (member reservations)
export const getUserReservations = (userId: string): ReservationData[] => {
  const reservations = getAllReservations();
  return reservations.filter(r => r.userId === userId && !r.isGuest);
};

// Get all guest reservations
export const getGuestReservations = (): ReservationData[] => {
  const reservations = getAllReservations();
  return reservations.filter(r => r.isGuest);
};

// Get all reservations (for manager portal)
export const getAllReservationsForManager = (): ReservationData[] => {
  return getAllReservations();
};

// Update reservation status
export const updateReservationStatus = (
  reservationId: string,
  status: ReservationData['status'],
  paymentStatus?: ReservationData['paymentStatus']
): boolean => {
  const reservations = getAllReservations();
  const index = reservations.findIndex(r => r.id === reservationId);
  
  if (index === -1) return false;
  
  reservations[index].status = status;
  if (paymentStatus) {
    reservations[index].paymentStatus = paymentStatus;
  }
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reservations));
  return true;
};

// Delete a reservation
export const deleteReservation = (reservationId: string): boolean => {
  const reservations = getAllReservations();
  const filtered = reservations.filter(r => r.id !== reservationId);
  
  if (filtered.length === reservations.length) return false;
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  return true;
};

// Get reservations for a specific accommodation that overlap with given dates
// This includes pending reservations to check capacity (waiting orders occupy capacity)
export const getOverlappingReservations = (
  accommodationId: string,
  checkIn: string,
  checkOut: string,
  excludeReservationId?: string
): ReservationData[] => {
  const reservations = getAllReservations();
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  
  return reservations.filter(r => {
    // Exclude cancelled and completed reservations, but include pending and confirmed
    // Pending/waiting orders should occupy capacity
    if (r.status === 'cancelled' || r.status === 'completed') return false;
    if (excludeReservationId && r.id === excludeReservationId) return false;
    if (r.itemId !== accommodationId || r.itemType !== 'accommodation') return false;
    
    const rCheckIn = new Date(r.checkIn);
    const rCheckOut = new Date(r.checkOut);
    
    // Check if dates overlap
    return rCheckIn < checkOutDate && rCheckOut > checkInDate;
  });
};

// Calculate occupied capacity for an accommodation on given dates
// This includes pending/waiting orders
export const getOccupiedCapacity = (
  accommodationId: string,
  checkIn: string,
  checkOut: string,
  excludeReservationId?: string
): number => {
  const overlappingReservations = getOverlappingReservations(accommodationId, checkIn, checkOut, excludeReservationId);
  return overlappingReservations.reduce((sum, r) => sum + r.guests, 0);
};

